import React, {
    useCallback, useState,
    useContext, useEffect
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { AuthorisedUserService } from 'tdic-capability-authorised-user';
import { useAuthentication, withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { withRouter } from 'react-router-dom';
import { ModalNextProvider } from '@jutro/components';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import { error } from '@jutro/logger';
import { TermsAndConditionUtils, EmailUtil } from 'gw-portals-util-js';
import messages from './ManageUser.messages';
import metadata from './ManageUser.metadata.json5';
import styles from './ManageUser.module.scss';


function ManageUserComponent(props) {
    const {
        tncTransactionTypesSubTypes, user_name: userName,
        email, user_id: userId, history, termData: terms
    } = props;
    const { authHeader } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [formData, setFormData] = useState({});
    const [submitStatus, updateSubmitStatus] = useState(undefined);
    const [disableFields, updateDisabledFields] = useState(false);
    const [userStatus, updateUserStatus] = useState(undefined);
    const [checkboxDetails, updateCheckboxDetails] = useState(false);
    const [dataAvailable, setDataValue] = useState({});
    const getActiveUsers = useCallback(() => {
        AuthorisedUserService.getActiveAuthorisedUser(authHeader).then((result) => {
            setFormData(result);
            setDataValue(result);
        }).catch((err) => {
            error(`ManageUser: accountDetails - Unable to get account details ${JSON.stringify(err)}`);
            ModalNextProvider.showAlert({
                title: messages.authorisedUserFailed,
                message: messages.requestFailed,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: messages.ok
            }).then(() => {
                history.push('/error');
            }, _.noop);
        });
    }, [authHeader, history]);


    useEffect(() => {
        getActiveUsers();
        // Call once when page is mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const invokeTncCall = useCallback((transactionSubtype, transactionDetails) => {
        TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''), tncTransactionTypesSubTypes,
            'AuthorisedUser', transactionSubtype, authHeader, transactionDetails, userName, email);
    }, [authHeader, email, terms, tncTransactionTypesSubTypes, userName]);

    const onSubmit = useCallback(() => {
        updateSubmitStatus(undefined);
        const params = {
            userName: _.get(formData, 'userName'),
            origin: 'microsoft',
            Name: {
                familyName: _.get(formData, 'lastName'),
                givenName: _.get(formData, 'firstName')
            },
            phoneNumbers: [
                {
                    value: _.get(formData, 'phoneNumber'),
                    type: 'Personal'
                }
            ],
            emails: [
                {
                    value: _.get(formData, 'userName'),
                    primary: 'true',
                    Type: 'Personal'
                }
            ]
        };

        AuthorisedUserService.createAuthorisedUserExt(params, authHeader)
            .then((response) => {
                if (response) {
                    updateSubmitStatus('success');
                    updateUserStatus('success');
                    const formDataClone = _.cloneDeep(formData);
                    _.set(formDataClone, 'isStaffCreated', false);
                    setFormData(formDataClone);
                    updateCheckboxDetails(false);
                    invokeTncCall('Enable', `${_.get(response, 'adaNumber', '')}:${userId}`);
                    updateDisabledFields(true);
                    getActiveUsers();
                }
            })
            .catch((err) => {
                error(`ManageUser: createAuthorisedUserExt -
             Unable to add authorized user ${JSON.stringify(err)}`);
                updateSubmitStatus('error');
                if (err.baseError && err.baseError.includes('621')) {
                    updateUserStatus('existingUserFound');
                } else {
                    updateUserStatus('authorisedUserFailedMsg');
                }
            });
    }, [formData, authHeader, invokeTncCall, userId, getActiveUsers]);

    const onValueChange = useCallback(
        (value, path) => {
            const formDataClone = _.cloneDeep(formData);
            _.set(formDataClone, path, value);
            setFormData(formDataClone);
        },
        [formData]
    );

    const changeCheckboxValue = useCallback(
        (value) => {
            updateCheckboxDetails(value);
        },
        []
    );

    const isFormDataValid = useCallback(() => {
        if (_.isEmpty(formData.phoneNumber) || _.includes(formData.phoneNumber, '_')) {
            return false;
        }
        if (_.isEmpty(formData.userName) || !(EmailUtil.checkEmailValidity(formData.userName))) {
            return false;
        }
        return !(_.isEmpty(formData.firstName) || _.isEmpty(formData.lastName));
    }, [formData]);


    const clearFormDataValues = useCallback(
        (value, path) => {
            const newDataClone = _.cloneDeep(disableFields);
            _.set(formData, 'firstName', '');
            _.set(formData, 'lastName', '');
            _.set(formData, 'userName', '');
            _.set(formData, 'phoneNumber', '');
            _.set(newDataClone, path, value);
            updateDisabledFields(newDataClone);
        },
        [disableFields, formData]
    );

    const enableAddButton = useCallback(() => {
        const isValid = isFormDataValid();
        if (!_.isEmpty(dataAvailable)) {
            return true;
        }
        return !(isValid && checkboxDetails);
    }, [checkboxDetails, dataAvailable, isFormDataValid]);

    const isFieldDisabled = useCallback(() => {
        if (_.isEmpty(dataAvailable) && checkboxDetails) {
            return false;
        }
        return true;
    }, [checkboxDetails, dataAvailable]);

    const deactivateUser = useCallback(() => {
        const requestPayLoad = {
            firstName: _.get(formData, 'firstName'),
            lastName: _.get(formData, 'lastName'),
            phoneNumber: _.get(formData, 'phoneNumber'),
            email: _.get(formData, 'userName')
        };
        AuthorisedUserService.deActivateAuthorisedUser(requestPayLoad,
            authHeader).then((result) => {
            if (result) {
                invokeTncCall('Deactivate', `${_.get(result, 'adaNumber', '')}:${userId}`);
                setDataValue('');
            }
        }).catch((err) => {
            error(`ManageUser: deactivateUser -
             Unable to deactivate user ${JSON.stringify(err)}`);
            ModalNextProvider.showAlert({
                title: messages.unableToDeactivate,
                message: messages.requestFailed,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: messages.ok
            })
                .then(() => {
                    updateSubmitStatus('error');
                    updateUserStatus('noAccount');
                }).then(_.noop);
        });
    }, [formData, authHeader, invokeTncCall, userId]);

    const onRemove = useCallback(
        () => {
            return ModalNextProvider.showConfirm({
                title: messages.removeUser,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.Ok,
                cancelButtonText: messages.cancel
            }).then((result) => {
                if (result === 'cancel') {
                    return _.noop();
                }
                deactivateUser();
                clearFormDataValues();
                updateSubmitStatus('success');
                updateUserStatus('remove');
                updateDisabledFields(false);
                updateCheckboxDetails(false);
                return _.noop();
            }, _.noop);
        },
        [clearFormDataValues, deactivateUser]
    );

    const enableDeactivateButton = useCallback(() => {
        if (!_.isEmpty(dataAvailable)) {
            if (checkboxDetails) {
                return false;
            }
            return true;
        }
        return !disableFields || !_.isEmpty(dataAvailable) || !checkboxDetails;
    }, [checkboxDetails, dataAvailable, disableFields]);

    const getDisclaimer = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.authorizedUserInfoId}
                values={{
                    not: <div className={styles.divUnderline}>not</div>
                }}
            />
        );
    }, []);

    const overrideProps = {
        '@field':
        {
            onValueChange: onValueChange
        },
        firstName: {
            disabled: isFieldDisabled()
        },
        lastName: {
            disabled: isFieldDisabled()
        },
        emailId: {
            disabled: isFieldDisabled()
        },
        phoneNumber: {
            disabled: isFieldDisabled()
        },
        userNotification: {
            type: submitStatus,
            message: translator(messages[userStatus]),
            visible: !_.isUndefined(submitStatus)
        },
        deActivateButton: {
            disabled: enableDeactivateButton()
        },
        createOfficeStaff: {
            label: messages.authorizedUserTnc,
            value: checkboxDetails
        },
        createButton: {
            disabled: enableAddButton()
        },
        dateCreated: {
            visible: !_.isEmpty(dataAvailable)
        },
        authorizedUserInfoId: {
            content: getDisclaimer()
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            changeCheckboxValue: changeCheckboxValue,
            onSubmit: onSubmit,
            onRemove: onRemove
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={formData}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

ManageUserComponent.propTypes = {
    value: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    }).isRequired,
    tncTransactionTypesSubTypes: PropTypes.shape({}).isRequired,
    user_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    user_id: PropTypes.string.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    termData: PropTypes.shape({}).isRequired
};
export default withRouter(withAuthenticationContext(
    withApplicationPropertyContext(ManageUserComponent)
));
